import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useCallback, useEffect, useMemo } from 'react';
import Sidebar from '../Sidebar';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TalentConnectorSettings from '../TalentConnector/TalentConnectorSettings';
import SubscriptionPopup from '../applicants/SubscriptionPopup';
import InviteMemberModal from '../Team/InviteMemberModal';
import '../Profile.css';
import { useProfilePageData } from './hooks/useProfilePageData';
import ProfileTopNav from './components/ProfileTopNav';
import CompanySection from './components/CompanySection';
import AccountSection from './components/AccountSection';
import BillingSection from './components/BillingSection';
import TeamAccessSection from './components/TeamAccessSection';
import ControlsSection from './components/ControlsSection';

const ProfilePage = () => {
  useUiLanguage();
  const profile = useProfilePageData();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const activeTab = params.get('tab') || 'account';
  const setActiveTab = useCallback((tab) => setParams({ tab }, { replace: true }), [setParams]);
  const { canManageTeam, canViewControls, setSidebarCollapsed, t } = profile;

  useEffect(() => {
    const narrowScreen = window.matchMedia?.('(max-width: 760px)');
    if (!narrowScreen) return;
    // Collapse when resizing into a narrow view; retain manual toggling otherwise.
    const collapseOnNarrowScreen = () => {
      if (narrowScreen.matches) setSidebarCollapsed(true);
    };
    collapseOnNarrowScreen();
    narrowScreen.addEventListener('change', collapseOnNarrowScreen);
    return () => narrowScreen.removeEventListener('change', collapseOnNarrowScreen);
  }, [setSidebarCollapsed]);

  const tabs = useMemo(() => {
    const baseTabs = [
      { id: 'account', label: t('tabs.account'), icon: 'fa-regular fa-user' },
      { id: 'billing', label: t('tabs.billing'), icon: 'fa-regular fa-credit-card' },
      { id: 'company', label: t('tabs.workspace'), icon: 'fa-regular fa-building' }
    ];

    if (canManageTeam) {
      baseTabs.push({
        id: 'team',
        label: t('tabs.access', 'Access'),
        icon: 'fa-solid fa-user-shield',
        iconTone: 'gold'
      });
    }

    if (canViewControls) {
      baseTabs.push({ id: 'controls', label: t('tabs.controls', 'Controls'), icon: 'fa-solid fa-sliders', iconTone: 'violet' });
    }

    return [
      ...baseTabs.map((tab) => ({ ...tab, group: 'workspace', groupLabel: t('settings.workspaceGroup') })),
      { id: 'connections', label: t('tabs.connections'), icon: 'fa-solid fa-plug', group: 'connector', groupLabel: t('settings.connectorGroup') },
      { id: 'usage', label: t('tabs.usage'), icon: 'fa-solid fa-chart-simple', group: 'connector', groupLabel: t('settings.connectorGroup') }
    ];
  }, [canManageTeam, canViewControls, t]);

  useEffect(() => {
    // Preserve old assistant shortlist links; lists live in Sourcing only.
    if (activeTab === 'shortlists') {
      navigate(`/sourcing?shortlist=${encodeURIComponent(params.get('shortlist') || '')}`, { replace: true });
      return;
    }
    if (!tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(tabs[0]?.id || 'company');
    }
  }, [activeTab, tabs, setActiveTab, navigate, params]);

  if (!profile.currentUser) {
    return null;
  }

  if (profile.profileLoading) {
    return (
      <div className="profile-page-container profile-settings-page" dir={profile.isRTL ? 'rtl' : 'ltr'} style={{ height: '100vh' }}>
        <Sidebar collapsed={profile.sidebarCollapsed} onToggle={profile.setSidebarCollapsed} />
        <div
          className="profile-shell"
          style={{
            marginInlineStart: profile.sidebarCollapsed ? '60px' : '240px',
            width: `calc(100vw - ${profile.sidebarCollapsed ? '60px' : '240px'})`
          }}
        >
          <div className="profile-loading-screen">
            <i className="fa-solid fa-spinner fa-spin"></i>
            <p>{uiText("Please Wait")}</p>
          </div>
        </div>
      </div>
    );
  }

  const mainContent = (() => {
    if (activeTab === 'connections' || activeTab === 'usage') {
      return <TalentConnectorSettings currentUser={profile.currentUser} section={activeTab} />;
    }
    if (activeTab === 'billing') {
      return (
        <BillingSection
          accessType={profile.accessType}
          handleManageSubscription={profile.handleManageSubscription}
          language={profile.language}
          onShowSubscriptionPopup={() => profile.setShowSubscriptionPopup(true)}
          subscriptionData={profile.subscriptionData}
          t={profile.t}
          trial={profile.trial}
        />
      );
    }
    if (activeTab === 'account') {
      return (
        <AccountSection
          accountEditing={profile.accountEditing}
          accountForm={profile.accountForm}
          accountSaving={profile.accountSaving}
          currentAccessProfile={profile.currentAccessProfile}
          currentUser={profile.currentUser}
          handleAccountEditToggle={profile.handleAccountEditToggle}
          handleAccountInputChange={profile.handleAccountInputChange}
          handleAccountSave={profile.handleAccountSave}
          handlePersonalPhotoChange={profile.handlePersonalPhotoChange}
          handlePersonalPhotoClick={profile.handlePersonalPhotoClick}
          handlePersonalPhotoRemove={profile.handlePersonalPhotoRemove}
          handleTransferOwnership={profile.handleTransferOwnership}
          isCompanyOwner={profile.isCompanyOwner}
          language={profile.language}
          personalFileInputRef={profile.personalFileInputRef}
          personalPhotoUploading={profile.personalPhotoUploading}
          personalPhotoRemoving={profile.personalPhotoRemoving}
          personalPhotoError={profile.personalPhotoError}
          personalPhotoUrl={profile.personalPhotoUrl}
          t={profile.t}
        />
      );
    }

    if (activeTab === 'team' && profile.canManageTeam) {
      return (
        <TeamAccessSection
          filteredPendingInvites={profile.filteredPendingInvites}
          filteredTeamMembers={profile.filteredTeamMembers}
          canEditTeamMember={profile.canEditTeamMember}
          canRemoveTeamMember={profile.canRemoveTeamMember}
          getTeamMemberActionStatus={profile.getTeamMemberActionStatus}
          getEnabledPermissionCount={profile.getEnabledPermissionCount}
          inviteMember={profile.inviteMember}
          language={profile.language}
          openPermissionsEditor={profile.openPermissionsEditor}
          removePendingInvite={profile.removePendingInvite}
          removeTeamMember={profile.removeTeamMember}
          searchTerm={profile.searchTerm}
          seatUsageCount={profile.seatUsageCount}
          setSearchTerm={profile.setSearchTerm}
          setTeamFilter={profile.setTeamFilter}
          subscriptionData={profile.subscriptionData}
          teamActionKey={profile.teamActionKey}
          teamFilter={profile.teamFilter}
          topPermissionLabels={profile.topPermissionLabels}
        />
      );
    }

    if (activeTab === 'controls' && profile.canViewControls) {
      return (
        <ControlsSection
          approvalBusy={profile.approvalBusy}
          approvalJobPostings={profile.approvalJobPostings}
          approvalOfferLetters={profile.approvalOfferLetters}
          approvalSettingsLoading={profile.approvalSettingsLoading}
          canManageCompanySettings={profile.canManageCompanySettings}
          companyData={profile.companyData}
          handleToggleCommunityFeature={profile.handleToggleCommunityFeature}
          handleToggleAgencyJobPosting={profile.handleToggleAgencyJobPosting}
          handleApplicationMethodChange={profile.handleApplicationMethodChange}
          applicationRoutingBusy={profile.applicationRoutingBusy}
          handleToggleJobApprovals={profile.handleToggleJobApprovals}
          handleToggleOfferApprovals={profile.handleToggleOfferApprovals}
          isRTL={profile.isRTL}
          t={profile.t}
          userUID={profile.userUID}
        />
      );
    }

    return (
        <CompanySection
          canManageCompanySettings={profile.canManageCompanySettings}
          companyCoverageLabel={profile.companyCoverageLabel}
          companyData={profile.companyData}
        companySaving={profile.companySaving}
        fileInputRef={profile.fileInputRef}
        handleInputChange={profile.handleInputChange}
        handleLogoChange={profile.handleLogoChange}
        handleLogoClick={profile.handleLogoClick}
          handleSave={profile.handleSave}
          isEditing={profile.isEditing}
          logoUploading={profile.logoUploading}
          onEditToggle={profile.handleEditToggle}
          pendingInvites={profile.pendingInvites}
          t={profile.t}
          teamMembers={profile.teamMembers}
        />
    );
  })();

  return (
    <div className="profile-page-container profile-settings-page" dir={profile.isRTL ? 'rtl' : 'ltr'} style={{ height: '100vh' }}>
      <Sidebar collapsed={profile.sidebarCollapsed} onToggle={profile.setSidebarCollapsed} />

      <div
        className="profile-shell"
        style={{
          marginInlineStart: profile.sidebarCollapsed ? '60px' : '240px',
          width: `calc(100vw - ${profile.sidebarCollapsed ? '60px' : '240px'})`,
          '--settings-sidebar-width': profile.sidebarCollapsed ? '60px' : '240px'
        }}
      >
        <div className="profile-shell-inner">
          <header className="settings-page-header">
            <div>
              <h1>{profile.t('settings.title')}</h1>
              <p>{profile.t('settings.description')}</p>
            </div>
            <div className="settings-workspace"><span className="settings-workspace-avatar">{profile.companyData.logoUrl ? <img src={profile.companyData.logoUrl} alt="" /> : (profile.companyData.companyName || 'J').charAt(0)}</span>
              <div><strong>{profile.companyData.companyName}</strong><span>{profile.t(profile.isCompanyOwner ? 'account.workspaceOwner' : 'collaborator')}</span></div></div>
          </header>
          <div className="settings-layout">
            <ProfileTopNav activeTab={activeTab} onTabChange={setActiveTab} tabs={tabs} label={profile.t('settings.navigation')} />
            <div className="profile-subpage" id="settings-content">{mainContent}</div>
          </div>
        </div>
      </div>

      <SubscriptionPopup
        isOpen={profile.showSubscriptionPopup}
        onClose={() => profile.setShowSubscriptionPopup(false)}
      />

      {profile.canManageTeam && (
        <InviteMemberModal
          isOpen={profile.showInviteModal}
          onClose={() => profile.setShowInviteModal(false)}
          onSubmit={profile.handleInviteSubmit}
          isLoading={profile.inviteLoading}
          departmentOptions={profile.departmentOptions}
          communityChannelOptions={profile.communityChannelOptions}
        />
      )}

      {profile.canManageTeam && profile.editingMember && (
        <InviteMemberModal
          isOpen={Boolean(profile.editingMember)}
          mode="edit"
          initialValues={profile.editingMember}
          onClose={() => profile.setEditingMember(null)}
          onSubmit={profile.savePermissions}
          isLoading={profile.memberEditorLoading}
          departmentOptions={profile.departmentOptions}
          communityChannelOptions={profile.communityChannelOptions}
        />
      )}
    </div>
  );
};

export default ProfilePage;
